import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateMenuOrderingSettingsOptions,
  BulkUpdateMenuOrderingSettingsResponse,
  BulkUpdateMenuOrderingSettingsTagsApplicationErrors,
  BulkUpdateMenuOrderingSettingsTagsByFilterApplicationErrors,
  BulkUpdateMenuOrderingSettingsTagsByFilterOptions,
  BulkUpdateMenuOrderingSettingsTagsByFilterResponse,
  BulkUpdateMenuOrderingSettingsTagsOptions,
  BulkUpdateMenuOrderingSettingsTagsResponse,
  ListMenusAvailabilityStatusOptions,
  ListMenusAvailabilityStatusResponse,
  MaskedMenuOrderingSettings,
  MenuOrderingSettings,
  MenuOrderingSettingsCreatedEnvelope,
  MenuOrderingSettingsDeletedEnvelope,
  MenuOrderingSettingsQuery,
  MenuOrderingSettingsQueryBuilder,
  MenuOrderingSettingsUpdatedEnvelope,
  QueryMenuOrderingSettingsResponse,
  TimeSlot,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateMenuOrderingSettings,
  UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings,
  UpsertMenuOrderingSettingsByMenuIdResponse,
  bulkUpdateMenuOrderingSettings as universalBulkUpdateMenuOrderingSettings,
  bulkUpdateMenuOrderingSettingsTags as universalBulkUpdateMenuOrderingSettingsTags,
  bulkUpdateMenuOrderingSettingsTagsByFilter as universalBulkUpdateMenuOrderingSettingsTagsByFilter,
  getMenuOrderingSettings as universalGetMenuOrderingSettings,
  listMenusAvailabilityStatus as universalListMenusAvailabilityStatus,
  queryMenuOrderingSettings as universalQueryMenuOrderingSettings,
  typedQueryMenuOrderingSettings as universalTypedQueryMenuOrderingSettings,
  updateExtendedFields as universalUpdateExtendedFields,
  updateMenuOrderingSettings as universalUpdateMenuOrderingSettings,
  upsertMenuOrderingSettingsByMenuId as universalUpsertMenuOrderingSettingsByMenuId,
} from './restaurants-menu-settings-v1-menu-ordering-settings-menu-ordering-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function getMenuOrderingSettings(
  httpClient: HttpClient
): GetMenuOrderingSettingsSignature {
  return (menuOrderingSettingsId: string) =>
    universalGetMenuOrderingSettings(
      menuOrderingSettingsId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMenuOrderingSettingsSignature {
  /**
   * Retrieves a menu ordering settings entity.
   * @param - ID of the menu ordering settings entity to retrieve.
   * @returns The requested menu ordering settings entity.
   */
  (menuOrderingSettingsId: string): Promise<
    NonNullablePaths<
      MenuOrderingSettings,
      | `availability.weeklyScheduleOptions.availableTimes`
      | `availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
      | `availability.timestampRangesOptions.ranges`
      | `availability.type`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function upsertMenuOrderingSettingsByMenuId(
  httpClient: HttpClient
): UpsertMenuOrderingSettingsByMenuIdSignature {
  return (
    menuId: string,
    menuOrderingSettings: UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings
  ) =>
    universalUpsertMenuOrderingSettingsByMenuId(
      menuId,
      menuOrderingSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpsertMenuOrderingSettingsByMenuIdSignature {
  /**
   * Upserts a menu ordering settings entity for a given `menuId`.
   * Try to create a menu ordering settings, in case it already exists - update the menu ordering settings
   * @param - ID of the menu these settings apply to.
   */
  (
    menuId: string,
    menuOrderingSettings: UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings
  ): Promise<
    NonNullablePaths<
      UpsertMenuOrderingSettingsByMenuIdResponse,
      | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes`
      | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
      | `menuOrderingSettings.availability.timestampRangesOptions.ranges`
      | `menuOrderingSettings.availability.type`
      | `menuOrderingSettings.tags.privateTags.tagIds`,
      7
    >
  >;
}

export function updateMenuOrderingSettings(
  httpClient: HttpClient
): UpdateMenuOrderingSettingsSignature {
  return (
    _id: string,
    menuOrderingSettings: NonNullablePaths<
      UpdateMenuOrderingSettings,
      `revision`,
      2
    >
  ) =>
    universalUpdateMenuOrderingSettings(
      _id,
      menuOrderingSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMenuOrderingSettingsSignature {
  /**
   * Updates a menu ordering settings entity.
   *
   * Each time the menu ordering settings entity is updated, `revision` increments by 1. The current `revision` must be specified when updating the menu ordering settings entity.
   * This ensures you're working with the latest entity and prevents unintended overwrites.
   * @param - Menu ordering settings object ID.
   * @returns Updated menu ordering settings entity.
   */
  (
    _id: string,
    menuOrderingSettings: NonNullablePaths<
      UpdateMenuOrderingSettings,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      MenuOrderingSettings,
      | `availability.weeklyScheduleOptions.availableTimes`
      | `availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
      | `availability.timestampRangesOptions.ranges`
      | `availability.type`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function queryMenuOrderingSettings(
  httpClient: HttpClient
): QueryMenuOrderingSettingsSignature {
  return () =>
    universalQueryMenuOrderingSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMenuOrderingSettingsSignature {
  /**
   * Creates a query to retrieve a list of menu ordering settings objects.
   *
   * The `queryMenuOrderingSettings()` function builds a query to retrieve a list of menu ordering settings objects and returns an `MenuOrderingSettingsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/menu-ordering-settings/menu-ordering-settings-query-builder/find) function.
   *
   * You can refine the query by chaining `MenuOrderingSettingsQueryBuilder` functions onto the query. `MenuOrderingSettingsQueryBuilder` functions enable you to filter, sort, and control the results that `queryMenuOrderingSettings()` returns.
   *
   * `queryMenuOrderingSettings()` runs with the following `MenuOrderingSettingsQueryBuilder` defaults, which you can override:
   *
   * - [`limit(50)`](/menu-ordering-settings/menu-ordering-settings-query-builder/limit)
   * - [ascending('_id')](/menu-ordering-settings/menu-ordering-settings-query-builder/ascending)
   *
   * The following `MenuOrderingSettingsQueryBuilder` functions are supported for `queryMenuOrderingSettings()`. For a full description of the menu ordering settings object, see the object returned for the [`items`](/menu-ordering-settings/menu-ordering-settings-query-result/items) property in `MenuOrderingSettingsQueryResult`.
   */
  (): MenuOrderingSettingsQueryBuilder;
}

export function typedQueryMenuOrderingSettings(
  httpClient: HttpClient
): TypedQueryMenuOrderingSettingsSignature {
  return (query: MenuOrderingSettingsQuery) =>
    universalTypedQueryMenuOrderingSettings(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryMenuOrderingSettingsSignature {
  /** */
  (query: MenuOrderingSettingsQuery): Promise<
    NonNullablePaths<
      QueryMenuOrderingSettingsResponse,
      | `menuOrderingSettings`
      | `menuOrderingSettings.${number}.availability.type`,
      5
    >
  >;
}

export function bulkUpdateMenuOrderingSettings(
  httpClient: HttpClient
): BulkUpdateMenuOrderingSettingsSignature {
  return (
    menusOrderingSettings: NonNullablePaths<
      MaskedMenuOrderingSettings,
      `menuOrderingSettings._id` | `menuOrderingSettings.revision`,
      3
    >[],
    options?: BulkUpdateMenuOrderingSettingsOptions
  ) =>
    universalBulkUpdateMenuOrderingSettings(
      menusOrderingSettings,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMenuOrderingSettingsSignature {
  /**
   * Updates multiple menu ordering settings entities at once.
   * Each time a menu ordering settings entity is updated, `revision` increments by 1. The existing revision must be specified when updating the menu ordering settings entity. This ensures you're working with the entity's latest information, and it prevents unintended overwrites.
   * @param - Menu ordering settings entities to update.
   */
  (
    menusOrderingSettings: NonNullablePaths<
      MaskedMenuOrderingSettings,
      `menuOrderingSettings._id` | `menuOrderingSettings.revision`,
      3
    >[],
    options?: BulkUpdateMenuOrderingSettingsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateMenuOrderingSettingsResponse,
      | `results`
      | `results.${number}.menuOrderingSettingsMetadata.originalIndex`
      | `results.${number}.menuOrderingSettingsMetadata.success`
      | `results.${number}.menuOrderingSettingsMetadata.error.code`
      | `results.${number}.menuOrderingSettingsMetadata.error.description`
      | `results.${number}.menuOrderingSettings.availability.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function listMenusAvailabilityStatus(
  httpClient: HttpClient
): ListMenusAvailabilityStatusSignature {
  return (
    timeSlot: NonNullablePaths<TimeSlot, `endTime` | `startTime`, 2>,
    options?: ListMenusAvailabilityStatusOptions
  ) =>
    universalListMenusAvailabilityStatus(
      timeSlot,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMenusAvailabilityStatusSignature {
  /**
   * Retrieves a list of a menu's availability statuses for a given time slot and restaurant operation. (See the Restaurants Operations API for more information.)
   * Returns the availability status for the given time slot per menu.
   * @param - The time slot for which to check the availability of menus.
   */
  (
    timeSlot: NonNullablePaths<TimeSlot, `endTime` | `startTime`, 2>,
    options?: ListMenusAvailabilityStatusOptions
  ): Promise<
    NonNullablePaths<
      ListMenusAvailabilityStatusResponse,
      | `menusAvailabilityStatus`
      | `menusAvailabilityStatus.${number}.menuId`
      | `menusAvailabilityStatus.${number}.availabilityStatus`,
      4
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a menu ordering settings entity without incrementing its revision.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes`
      | `menuOrderingSettings.availability.weeklyScheduleOptions.availableTimes.${number}.dayOfWeek`
      | `menuOrderingSettings.availability.timestampRangesOptions.ranges`
      | `menuOrderingSettings.availability.type`
      | `menuOrderingSettings.tags.privateTags.tagIds`,
      7
    >
  >;
}

export function bulkUpdateMenuOrderingSettingsTags(
  httpClient: HttpClient
): BulkUpdateMenuOrderingSettingsTagsSignature {
  return (
    menuOrderingSettingsIds: string[],
    options?: BulkUpdateMenuOrderingSettingsTagsOptions
  ) =>
    universalBulkUpdateMenuOrderingSettingsTags(
      menuOrderingSettingsIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMenuOrderingSettingsTagsSignature {
  /**
   * Synchronously update tags on multiple menu ordering settings entities.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the menu ordering settings entities to update tags for.
   */
  (
    menuOrderingSettingsIds: string[],
    options?: BulkUpdateMenuOrderingSettingsTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateMenuOrderingSettingsTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateMenuOrderingSettingsTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateMenuOrderingSettingsTagsByFilter(
  httpClient: HttpClient
): BulkUpdateMenuOrderingSettingsTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateMenuOrderingSettingsTagsByFilterOptions
  ) =>
    universalBulkUpdateMenuOrderingSettingsTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMenuOrderingSettingsTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple menu ordering settings entities according to the specified filter.
   * If a filter isn't specified, this method updates all menu ordering settings entities.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which menu ordering settings entities to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateMenuOrderingSettingsTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateMenuOrderingSettingsTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateMenuOrderingSettingsTagsByFilterApplicationErrors;
    }
  >;
}

export const onMenuOrderingSettingsCreated = EventDefinition(
  'wix.restaurants.menu_settings.v1.menu_ordering_settings_created',
  true,
  (event: MenuOrderingSettingsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MenuOrderingSettingsCreatedEnvelope>();
export const onMenuOrderingSettingsDeleted = EventDefinition(
  'wix.restaurants.menu_settings.v1.menu_ordering_settings_deleted',
  true,
  (event: MenuOrderingSettingsDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MenuOrderingSettingsDeletedEnvelope>();
export const onMenuOrderingSettingsUpdated = EventDefinition(
  'wix.restaurants.menu_settings.v1.menu_ordering_settings_updated',
  true,
  (event: MenuOrderingSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'entity.availability.timestampRangesOptions.ranges.endTime',
            },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            {
              path: 'modifiedFields.availability.timestampRangesOptions.ranges.startTime',
            },
            {
              path: 'modifiedFields.availability.timestampRangesOptions.ranges.endTime',
            },
          ],
        },
      ])
    )
)<MenuOrderingSettingsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  App,
  ApplicationError,
  Availability,
  AvailabilityAvailabilityTypeOptionsOneOf,
  AvailabilityStatus,
  AvailabilityType,
  AvailableRange,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkMenuOrderingSettingsResult,
  BulkUpdateMenuOrderingSettingsOptions,
  BulkUpdateMenuOrderingSettingsRequest,
  BulkUpdateMenuOrderingSettingsResponse,
  BulkUpdateMenuOrderingSettingsTagsByFilterOptions,
  BulkUpdateMenuOrderingSettingsTagsByFilterRequest,
  BulkUpdateMenuOrderingSettingsTagsByFilterResponse,
  BulkUpdateMenuOrderingSettingsTagsOptions,
  BulkUpdateMenuOrderingSettingsTagsRequest,
  BulkUpdateMenuOrderingSettingsTagsResponse,
  BulkUpdateMenuOrderingSettingsTagsResult,
  BusinessSchedule,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CommonBulkActionMetadata,
  CommonItemMetadata,
  ConsentPolicy,
  CreateMenuOrderingSettingsRequest,
  CreateMenuOrderingSettingsResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DayOfWeek,
  DayOfWeekAvailability,
  DeleteMenuOrderingSettingsRequest,
  DeleteMenuOrderingSettingsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntitiesDayOfWeek,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  File,
  GeoCoordinates,
  GetMenuOrderingSettingsRequest,
  GetMenuOrderingSettingsResponse,
  GetRestaurantsAppClonesStatusRequest,
  GetRestaurantsAppClonesStatusResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ListMenusAvailabilityStatusOptions,
  ListMenusAvailabilityStatusRequest,
  ListMenusAvailabilityStatusResponse,
  Locale,
  MaskedMenuOrderingSettings,
  MenuAvailabilityStatus,
  MenuOrderingSettings,
  MenuOrderingSettingsCreatedEnvelope,
  MenuOrderingSettingsDeletedEnvelope,
  MenuOrderingSettingsQueryBuilder,
  MenuOrderingSettingsQueryResult,
  MenuOrderingSettingsQuerySpec,
  MenuOrderingSettingsUpdatedEnvelope,
  MenusDataCloningCompleted,
  MessageEnvelope,
  Multilingual,
  OperationsDataCloningCompleted,
  Page,
  Pages,
  PlacementType,
  Properties,
  PropertiesChange,
  QueryMenuOrderingSettingsRequest,
  QueryMenuOrderingSettingsResponse,
  ResolutionMethod,
  RestaurantsAppCloneStatus,
  RestoreInfo,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  SupportedLanguage,
  TagList,
  Tags,
  TimeOfDay,
  TimeOfDayRange,
  TimePeriod,
  TimeSlot,
  TimestampRangesOptions,
  Translation,
  URI,
  URIs,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateMenuOrderingSettings,
  UpdateMenuOrderingSettingsRequest,
  UpdateMenuOrderingSettingsResponse,
  UpsertMenuOrderingSettingsByMenuIdMenuOrderingSettings,
  UpsertMenuOrderingSettingsByMenuIdRequest,
  UpsertMenuOrderingSettingsByMenuIdResponse,
  WebhookIdentityType,
  WeeklyScheduleOptions,
  utils,
} from './restaurants-menu-settings-v1-menu-ordering-settings-menu-ordering-settings.universal.js';
